import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SidebarLayout from "../../PageLayout/SidebarLayout";
import AuthorizedInfo from "../components/AuthorizedInfo";
import AddressInfo from "../components/AddressInfo";
import IntegrationsTab from "../components/IntegrationsTab";
import useUserProfile from "../../hooks/useUserProfile";
import { getMfaStatus } from "../../services/mfa.service";

const TABS = [
  { id: "general", label: "General" },
  { id: "integrations", label: "Integrations" },
];

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error } = useUserProfile();
  const authorizedInfo = data?.authorizedInfo || null;
  const addressInfo = data?.addressInfo || null;
  const locationOptions = data?.locationOptions || { provinces: [], districts: [] };
  const [isMfaEnabled, setIsMfaEnabled] = useState(() => {
    try { return localStorage.getItem("mfaEnabled") === "1"; } catch { return false; }
  });
  const mfaRequired =
    location?.state?.mfaRequired === true ||
    (typeof location?.pathname === "string" && /\/settings\/security\/mfa-setup\b/.test(location.pathname));

  const activeTab = searchParams.get("tab") === "integrations" ? "integrations" : "general";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // If authenticated, fetch server status to ensure label is accurate
        let authed = false;
        try { authed = !!localStorage.getItem("authToken"); } catch {}
        if (!authed) return;
        const status = await getMfaStatus().catch(() => ({}));
        const enabled = !!(status?.mfa_enabled || status?.enabled || status?.is_enabled);
        if (!cancelled) {
          setIsMfaEnabled(enabled);
          try { localStorage.setItem("mfaEnabled", enabled ? "1" : "0"); } catch {}
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const mfaCtaLabel = isMfaEnabled ? "Disable MFA" : "Enable MFA";
  const handleMfaCta = () => {
    navigate("/settings/security/mfa-setup");
  };

  const handleTabChange = (tabId) => {
    if (tabId === "general") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: tabId });
    }
  };

  return (
    <SidebarLayout>
      <div className="p-4 md:p-6 space-y-4 max-w-[1400px] mx-auto w-full min-w-0">
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-6" aria-label="Settings tabs">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabChange(tab.id)}
                  className={`pb-3 text-sm border-b-2 -mb-px transition-colors ${
                    isActive
                      ? "border-slate-900 text-slate-900 font-semibold"
                      : "border-transparent text-gray-500 hover:text-gray-700 font-medium"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {activeTab === "general" && (
          <div>
            {mfaRequired && (
              <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 text-yellow-900 px-4 py-3 text-sm">
                Multi-Factor Authentication (MFA) is required for admin actions. Please enable MFA below.
              </div>
            )}

            {/* Security: MFA entry point */}
            <div className="mb-6">
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-gray-900">Multi‑Factor Authentication (MFA)</div>
                  <div className="text-xs text-gray-500">Add an extra layer of security to your account.</div>
                </div>
                <button
                  type="button"
                  onClick={handleMfaCta}
                  className="inline-flex items-center gap-2 rounded-md bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium px-4 py-2"
                >
                  {mfaCtaLabel}
                </button>
              </div>
            </div>

            {/* Sections */}
            <div className="mb-6">
            {authorizedInfo ? (
              <AuthorizedInfo initialData={authorizedInfo} />
            ) : (
              <div className="text-sm text-gray-500">{loading ? "Loading profile..." : (error || "No profile data")}</div>
            )}
            </div>
            {addressInfo ? (
              <AddressInfo addressInfo={addressInfo} locationOptions={locationOptions} />
            ) : null}
          </div>
        )}

        {activeTab === "integrations" && <IntegrationsTab />}
      </div>
    </SidebarLayout>
  );
}
